import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  useListTaskCategories,
  useCreateTaskCategory,
  useUpdateTaskCategory,
  useDeleteTaskCategory,
  getListTaskCategoriesQueryKey,
  useListSlaPolicies,
  useListRoutingRules,
  useListSpaces,
  getListSpacesQueryKey,
  useCopyTaskConfig,
} from "@workspace/api-client-react";
import type { TaskCategory, TaskPriority } from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tag, ShieldAlert, Copy } from "lucide-react";

// Phase 41 Plan 10 (Wave 7) — TCFG-01 Settings CRUD for task_category, the
// first of four config screens appended to the Tasks nav group 41-09
// created. Table+Dialog CRUD shape copied from
// artifacts/crm/src/components/inventory/categories-tab.tsx; only
// GENERATED hooks are consumed (no hand-written fetch/Zod).
//
// TPRM-05: gated to admin+ops_lead. The server (canEditConfig) is the real
// authority — every mutation 403s for anyone else — this page additionally
// hides the whole editing surface for other roles (defense-in-depth UX,
// not a security boundary) and surfaces the server's 403 verbatim if a
// mutation somehow slips through (e.g. a role changed mid-session).
//
// `type` has no counterpart in UpdateTaskCategoryBody -> immutable after
// create (drives task-create.tsx's per-type form shape), so the Edit
// dialog shows it read-only instead of an editable Select.
//
// "Delete" here is the server's soft archive (active=false) — task rows
// referencing a category are never orphaned. The Edit dialog's Active
// switch is the only way back (un-archive).
//
// Task 3 (this file, second edit) adds the "Copy config from space" admin
// action (TCFG-08) — see the block near the bottom of this component.

const TASK_TYPES = ["complaint", "request", "maintenance", "assignment", "feedback_followup", "operational"] as const;
const PRIORITIES: TaskPriority[] = ["P1", "P2", "P3", "P4"];

const CreateSchema = z.object({
  type: z.enum(TASK_TYPES),
  name: z.string().min(1, "Category name is required"),
  defaultPriority: z.enum(["P1", "P2", "P3", "P4"]),
  slaPolicyId: z.number().nullable(),
  routingRuleId: z.number().nullable(),
  sort: z.number().int(),
});
const EditSchema = z.object({
  name: z.string().min(1, "Category name is required"),
  defaultPriority: z.enum(["P1", "P2", "P3", "P4"]),
  slaPolicyId: z.number().nullable(),
  routingRuleId: z.number().nullable(),
  sort: z.number().int(),
  active: z.boolean(),
});
type CreateValues = z.infer<typeof CreateSchema>;
type EditValues = z.infer<typeof EditSchema>;

const EMPTY_CREATE: CreateValues = {
  type: "complaint",
  name: "",
  defaultPriority: "P3",
  slaPolicyId: null,
  routingRuleId: null,
  sort: 0,
};

/** Reads the shape customFetch's ApiError throws (.status + parsed .data) — mirrors task-detail.tsx's helper. */
function errStatus(err: unknown): number | undefined {
  return (err as { status?: number })?.status;
}
function errData(err: unknown): Record<string, unknown> | undefined {
  return (err as { data?: Record<string, unknown> })?.data;
}

export default function TaskCategorySettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const canEdit = isAdmin || user?.role === "ops_lead";

  const queryClient = useQueryClient();
  // GET routes are open to any authenticated role server-side — the `canEdit`
  // gate below controls whether this page RENDERS the CRUD UI at all, not
  // whether the underlying data can be fetched. Fetching unconditionally
  // keeps the hook call shape stable (no options needed) and matches the
  // list-on-mount convention of categories-tab.tsx.
  const { data: categories, isLoading } = useListTaskCategories();
  const { data: slaPolicies } = useListSlaPolicies();
  const { data: routingRules } = useListRoutingRules();
  const { data: spaces } = useListSpaces({ query: { enabled: isAdmin, queryKey: getListSpacesQueryKey() } });

  const [openCreate, setOpenCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<TaskCategory | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<TaskCategory | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyToSpaceId, setCopyToSpaceId] = useState<string>("");

  const createMutation = useCreateTaskCategory();
  const updateMutation = useUpdateTaskCategory();
  const archiveMutation = useDeleteTaskCategory();
  const copyMutation = useCopyTaskConfig();

  const createForm = useForm<CreateValues>({ resolver: zodResolver(CreateSchema), defaultValues: EMPTY_CREATE });
  const editForm = useForm<EditValues>({
    resolver: zodResolver(EditSchema),
    defaultValues: { name: "", defaultPriority: "P3", slaPolicyId: null, routingRuleId: null, sort: 0, active: true },
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: getListTaskCategoriesQueryKey() });
  }

  function openEdit(cat: TaskCategory) {
    setEditTarget(cat);
    editForm.reset({
      name: cat.name,
      defaultPriority: cat.defaultPriority,
      slaPolicyId: cat.slaPolicyId ?? null,
      routingRuleId: cat.routingRuleId ?? null,
      sort: cat.sort,
      active: cat.active,
    });
  }
  function closeDialogs() {
    setOpenCreate(false);
    setEditTarget(null);
    createForm.reset(EMPTY_CREATE);
  }

  async function onCreate(values: CreateValues) {
    try {
      await createMutation.mutateAsync({ data: values });
      toast.success(`Created category "${values.name}".`);
      invalidate();
      closeDialogs();
    } catch (err) {
      const status = errStatus(err);
      if (status === 403) {
        toast.error((errData(err)?.message as string) ?? "You don't have permission to add categories.");
        return;
      }
      toast.error("Could not create the category. Please try again.");
    }
  }

  async function onEdit(values: EditValues) {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ id: editTarget.id, data: values });
      toast.success("Category saved.");
      invalidate();
      closeDialogs();
    } catch (err) {
      const status = errStatus(err);
      if (status === 403) {
        toast.error((errData(err)?.message as string) ?? "You don't have permission to edit categories.");
        return;
      }
      toast.error("Could not save the category. Please try again.");
    }
  }

  async function onArchive() {
    if (!archiveTarget) return;
    try {
      await archiveMutation.mutateAsync({ id: archiveTarget.id });
      toast.success(`Archived "${archiveTarget.name}".`);
      invalidate();
      setArchiveTarget(null);
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to archive categories.")
          : "Could not archive the category.",
      );
    }
  }

  // TCFG-08 — "Copy config from space" (Task 3). Visible only to admin per
  // this plan's explicit instruction (stricter than the server, which gates
  // every /task-config/* mutation — copy included — on the SAME
  // canEditConfig predicate as every other config mutation, i.e.
  // admin+ops_lead; see 41-08-SUMMARY.md's key-decision #1). Hiding it from
  // ops_lead here is a deliberate, MORE restrictive UI choice, not a
  // security boundary — the server would still accept the call from
  // ops_lead if it somehow reached it.
  async function onCopy() {
    if (!user?.spaceId || !copyToSpaceId) return;
    try {
      const result = await copyMutation.mutateAsync({
        data: { fromSpaceId: user.spaceId, toSpaceId: Number(copyToSpaceId) },
      });
      toast.success(
        `Copied config: ${result.categoriesCopied} categories, ${result.slaPoliciesCopied} SLA policies, ${result.routingRulesCopied} routing rules, ${result.escalationRulesCopied} escalation rules.`,
      );
      setCopyOpen(false);
      setCopyToSpaceId("");
      invalidate();
    } catch (err) {
      const status = errStatus(err);
      if (status === 409) {
        toast.error("That space already has task config — copy skipped to avoid a silent merge.");
        return;
      }
      if (status === 403) {
        toast.error((errData(err)?.message as string) ?? "You don't have permission to copy config.");
        return;
      }
      toast.error("Could not copy config. Please try again.");
    }
  }

  function slaLabel(id: number | null | undefined): string {
    if (id == null) return "—";
    const p = slaPolicies?.find((s) => s.id === id);
    return p ? (p.name ?? `${p.priority} SLA`) : `#${id}`;
  }
  function routingLabel(id: number | null | undefined): string {
    if (id == null) return "—";
    const r = routingRules?.find((r2) => r2.id === id);
    return r ? `${r.matchType} → ${r.assignToRole}` : `#${id}`;
  }

  if (!canEdit) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Restricted</CardTitle>
          <CardDescription>Task category settings are limited to admin and ops lead roles.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Tag className="h-6 w-6 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Task Categories</h1>
            <p className="text-sm text-muted-foreground">
              Zero-code-change config — categories drive the create form's dropdown and its default SLA/routing.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setCopyOpen(true)}>
              <Copy className="mr-2 h-4 w-4" /> Copy config from space
            </Button>
          )}
          <Button onClick={() => setOpenCreate(true)}>Add Category</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Categories {categories ? <span className="text-muted-foreground font-normal text-sm">({categories.length})</span> : null}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>SLA Policy</TableHead>
                <TableHead>Routing Rule</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right w-[160px] pr-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(4)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : categories?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                    No categories yet — add the first one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                categories?.map((cat) => (
                  <TableRow key={cat.id}>
                    <TableCell className="capitalize text-sm">{cat.type.replace(/_/g, " ")}</TableCell>
                    <TableCell className="font-medium">{cat.name}</TableCell>
                    <TableCell><Badge variant="secondary">{cat.defaultPriority}</Badge></TableCell>
                    <TableCell className="text-sm">{slaLabel(cat.slaPolicyId)}</TableCell>
                    <TableCell className="text-sm">{routingLabel(cat.routingRuleId)}</TableCell>
                    <TableCell>
                      <Badge variant={cat.active ? "default" : "outline"}>{cat.active ? "Active" : "Archived"}</Badge>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap pr-4">
                      <div className="flex justify-end items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(cat)}>Edit</Button>
                        {cat.active && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setArchiveTarget(cat)}
                          >
                            Archive
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={openCreate || editTarget !== null} onOpenChange={(o) => !o && closeDialogs()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editTarget ? `Edit ${editTarget.name}` : "Add Category"}</DialogTitle>
          </DialogHeader>
          {editTarget ? (
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(onEdit)} className="space-y-3">
                <div className="text-sm">
                  <span className="text-muted-foreground">Type: </span>
                  <span className="capitalize font-medium">{editTarget.type.replace(/_/g, " ")}</span>
                  <span className="text-xs text-muted-foreground"> (fixed at creation)</span>
                </div>
                <FormField name="name" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="defaultPriority" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Priority</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="slaPolicyId" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>SLA Policy</FormLabel>
                    <Select value={field.value != null ? String(field.value) : "__none__"} onValueChange={(v) => field.onChange(v === "__none__" ? null : Number(v))}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {slaPolicies?.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name ?? `${s.priority} SLA`}{!s.active ? " (archived)" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="routingRuleId" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Routing Rule</FormLabel>
                    <Select value={field.value != null ? String(field.value) : "__none__"} onValueChange={(v) => field.onChange(v === "__none__" ? null : Number(v))}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {routingRules?.map((r) => <SelectItem key={r.id} value={String(r.id)}>{r.matchType} → {r.assignToRole}{!r.active ? " (archived)" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="sort" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sort</FormLabel>
                    <FormControl>
                      <Input type="number" value={field.value} onChange={(e) => field.onChange(Number(e.target.value) || 0)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="active" control={editForm.control} render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <FormLabel>Active</FormLabel>
                      <p className="text-xs text-muted-foreground">Off = archived; won't appear on the create form's dropdown.</p>
                    </div>
                    <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  </FormItem>
                )} />
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={closeDialogs}>Discard changes</Button>
                  <Button type="submit" disabled={updateMutation.isPending}>{updateMutation.isPending ? "Saving…" : "Save Category"}</Button>
                </DialogFooter>
              </form>
            </Form>
          ) : (
            <Form {...createForm}>
              <form onSubmit={createForm.handleSubmit(onCreate)} className="space-y-3">
                <FormField name="type" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {TASK_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="name" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input placeholder="e.g. AC not working" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="defaultPriority" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Priority</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="slaPolicyId" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>SLA Policy</FormLabel>
                    <Select value={field.value != null ? String(field.value) : "__none__"} onValueChange={(v) => field.onChange(v === "__none__" ? null : Number(v))}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {slaPolicies?.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name ?? `${s.priority} SLA`}{!s.active ? " (archived)" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="routingRuleId" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Routing Rule</FormLabel>
                    <Select value={field.value != null ? String(field.value) : "__none__"} onValueChange={(v) => field.onChange(v === "__none__" ? null : Number(v))}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {routingRules?.map((r) => <SelectItem key={r.id} value={String(r.id)}>{r.matchType} → {r.assignToRole}{!r.active ? " (archived)" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="sort" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sort</FormLabel>
                    <FormControl>
                      <Input type="number" value={field.value} onChange={(e) => field.onChange(Number(e.target.value) || 0)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={closeDialogs}>Discard changes</Button>
                  <Button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? "Creating…" : "Save Category"}</Button>
                </DialogFooter>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive confirmation */}
      <AlertDialog open={archiveTarget !== null} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive category {archiveTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Sets it to inactive — it disappears from the create form's dropdown, but existing tasks that
              already reference it are unaffected. Reversible from Edit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onArchive} disabled={archiveMutation.isPending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {archiveMutation.isPending ? "Archiving…" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy config from space — TCFG-08, admin-only per this plan (server allows admin+ops_lead) */}
      <Dialog open={copyOpen} onOpenChange={(o) => !o && setCopyOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy config from space</DialogTitle>
            <DialogDescription>
              Copies ALL four config tables (categories, SLA policies, routing rules, escalation rules) from your
              space into the target space in one shot — this is how opening a new franchise gets its config
              without any code change. Blocked with a 409 if the target already has any config rows (never a
              silent merge).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm">
              <span className="text-muted-foreground">From: </span>
              <span className="font-medium">{spaces?.find((s) => s.id === user?.spaceId)?.name ?? `Space #${user?.spaceId}`}</span>
            </div>
            <div>
              <label className="text-sm font-medium">To</label>
              <Select value={copyToSpaceId} onValueChange={setCopyToSpaceId}>
                <SelectTrigger><SelectValue placeholder="Pick a target space" /></SelectTrigger>
                <SelectContent>
                  {spaces?.filter((s) => s.id !== user?.spaceId).map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyOpen(false)}>Cancel</Button>
            <Button onClick={onCopy} disabled={copyMutation.isPending || !copyToSpaceId}>
              {copyMutation.isPending ? "Copying…" : "Copy config"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
